import React, { useCallback, useMemo, useState } from "react";
import {
  Ban,
  Crown,
  Search,
  Shield,
  ShieldCheck,
  Trash2,
  UserCheck,
} from "lucide-react";

import Topbar from "../components/Topbar";
import { Drawer, Field, Section } from "../components/ui/Drawer";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  TableSkeleton,
  useToast,
} from "../components/ui/Feedback";
import {
  Avatar,
  FilterTabs,
  Pagination,
  useDebounced,
} from "../components/ui/Table";
import { useAdminAuth } from "../context/AdminAuth";
import { useQuery } from "../lib/useQuery";
import {
  fetchUserDetail,
  fetchUsers,
  setAdmin,
  setPremium,
  setSuspension,
} from "../lib/api";
import type { AdminUser, UserFilter } from "../lib/types";

/**
 * Users.
 *
 * Rebuilt around `admin_users` (migration 0008) rather than
 * `select('*').from('profiles')`. The old query pulled every column of every
 * row — every email, phone number, billing address and hair profile in the
 * database — into the browser on page load, then paginated and searched
 * client-side, which is both a data-exposure problem and one that gets slower
 * every day.
 *
 * Actions go through the `admin_*` RPCs. They check authorisation server-side
 * and write an audit row, which the old direct `.update()` calls did neither
 * of — and those calls also discarded their error, so a write denied by RLS
 * left the row looking changed until you reloaded.
 */

const PAGE_SIZE = 50;

const FILTERS: { key: UserFilter; label: string; tone?: string }[] = [
  { key: "all", label: "All" },
  { key: "premium", label: "Premium", tone: "#d99e00" },
  { key: "free", label: "Free", tone: "#19b159" },
  { key: "suspended", label: "Suspended", tone: "var(--danger)" },
  { key: "deleting", label: "Deleting", tone: "#e06a2b" },
  { key: "admins", label: "Admins", tone: "#6c5ffc" },
];

const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

type PendingAction =
  | { kind: "suspend"; user: AdminUser }
  | { kind: "unsuspend"; user: AdminUser }
  | { kind: "premium"; user: AdminUser; next: boolean }
  | { kind: "admin"; user: AdminUser; next: boolean };

export default function Users() {
  const toast = useToast();
  const { identity } = useAdminAuth();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<UserFilter>("all");
  const [offset, setOffset] = useState(0);
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState(false);

  const debouncedSearch = useDebounced(search);

  const list = useQuery(
    () => fetchUsers({ search: debouncedSearch, filter, limit: PAGE_SIZE, offset }),
    [debouncedSearch, filter, offset]
  );

  const detail = useQuery(
    () => (openUser ? fetchUserDetail(openUser) : Promise.resolve(null)),
    [openUser]
  );

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;

  // Changing the filter or the search while on page 4 would otherwise request
  // an offset past the end of the new result set and show an empty table.
  const changeFilter = useCallback((next: UserFilter) => {
    setFilter(next);
    setOffset(0);
  }, []);
  const changeSearch = useCallback((next: string) => {
    setSearch(next);
    setOffset(0);
  }, []);

  const runAction = useCallback(
    async (reason: string) => {
      if (!pending) return;
      setBusy(true);
      try {
        switch (pending.kind) {
          case "suspend":
            await setSuspension(pending.user.id, true, reason);
            toast.success(`${pending.user.full_name ?? "Account"} suspended`);
            break;
          case "unsuspend":
            await setSuspension(pending.user.id, false);
            toast.success("Suspension lifted");
            break;
          case "premium":
            await setPremium(pending.user.id, pending.next, reason);
            toast.success(pending.next ? "Premium granted" : "Premium revoked");
            break;
          case "admin":
            await setAdmin(pending.user.id, pending.next);
            toast.success(pending.next ? "Admin access granted" : "Admin access revoked");
            break;
        }
        setPending(null);
        list.refetch();
        if (openUser) detail.refetch();
      } catch (err) {
        // Left open so the reason typed isn't lost to a transient failure.
        toast.failure(err);
      } finally {
        setBusy(false);
      }
    },
    [pending, toast, list, detail, openUser]
  );

  const confirmCopy = useMemo(() => {
    if (!pending) return null;
    const name = pending.user.full_name ?? pending.user.email ?? "this account";
    switch (pending.kind) {
      case "suspend":
        return {
          title: "Suspend this account?",
          body: `${name} will be signed out of the app and shown your reason. Their posts stay in the database but leave every feed.`,
          confirmLabel: "Suspend",
          destructive: true,
          requireReason: true,
          reasonLabel: "Reason (shown to the user)",
        };
      case "unsuspend":
        return {
          title: "Lift this suspension?",
          body: `${name} gets full access back immediately.`,
          confirmLabel: "Lift suspension",
        };
      case "premium":
        return {
          title: pending.next ? "Grant Premium?" : "Revoke Premium?",
          body: pending.next ? (
            <>
              Gives {name} unlimited Zora messages and the Premium badge.
              <br />
              <br />
              <strong>This is an override, not a subscription.</strong> Stripe owns{" "}
              <code>is_premium</code>, so the next subscription event for this
              customer will overwrite whatever you set here. Use it to comp
              someone or to repair a missed webhook.
            </>
          ) : (
            `Removes Premium from ${name} straight away.`
          ),
          confirmLabel: pending.next ? "Grant Premium" : "Revoke Premium",
          requireReason: true,
          reasonLabel: "Note for the audit log",
        };
      case "admin":
        return {
          title: pending.next ? "Grant admin access?" : "Revoke admin access?",
          body: pending.next ? (
            <>
              {name} will be able to sign into this panel and do everything you
              can — including suspending accounts and promoting other admins.
              <br />
              <br />
              There is no lesser role. Only do this for someone you would trust
              with the database.
            </>
          ) : (
            `${name} loses access to this panel immediately.`
          ),
          confirmLabel: pending.next ? "Grant admin" : "Revoke admin",
          destructive: !pending.next,
        };
    }
  }, [pending]);

  return (
    <div>
      <Topbar title="Users" />
      <div className="page-shell">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
          <FilterTabs tabs={FILTERS} active={filter} onChange={changeFilter} />
          <div style={{ position: "relative", minWidth: 260, flex: "0 1 320px" }}>
            <Search
              size={14}
              color="var(--text-muted)"
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              className="input"
              placeholder="Search name, username or email…"
              style={{ paddingLeft: 34 }}
              value={search}
              onChange={(e) => changeSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="card">
          {list.error ? (
            <ErrorState error={list.error} onRetry={list.refetch} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Activity</th>
                      <th>Points</th>
                      <th>Joined</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading && <TableSkeleton rows={8} cols={7} />}

                    {!list.loading &&
                      rows.map((user) => (
                        <tr
                          key={user.id}
                          onClick={() => setOpenUser(user.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={user.full_name} url={user.avatar_url} seed={user.id} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
                                  {user.full_name ?? "—"}
                                  {user.is_admin && (
                                    <span className="badge badge-primary" style={{ fontSize: 10 }}>
                                      <ShieldCheck size={10} /> Admin
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
                                  {user.email ?? "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 12.5 }}>
                              {user.posts_count} post{user.posts_count === 1 ? "" : "s"} · {user.orders_count} order
                              {user.orders_count === 1 ? "" : "s"}
                            </div>
                            {user.streak_count > 0 && (
                              <div style={{ fontSize: 11.5, color: "#e06a2b", fontWeight: 700, marginTop: 2 }}>
                                🔥 {user.streak_count} day streak
                              </div>
                            )}
                          </td>
                          <td style={{ fontWeight: 700, color: "var(--primary)" }}>{user.points ?? 0}</td>
                          <td>{date(user.created_at)}</td>
                          <td>
                            {user.is_premium ? (
                              <span className="badge badge-warning">
                                <Crown size={10} /> {user.premium_plan ?? "Premium"}
                              </span>
                            ) : (
                              <span className="badge">Free</span>
                            )}
                          </td>
                          <td>
                            {user.suspended_at ? (
                              <span className="badge badge-danger">Suspended</span>
                            ) : user.deactivated_at ? (
                              <span className="badge" style={{ background: "#fdf0e6", color: "#b4501a" }}>
                                Deleting
                              </span>
                            ) : (
                              <span className="badge badge-success">Active</span>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button
                                className="btn btn-sm"
                                title={user.is_premium ? "Revoke Premium" : "Grant Premium"}
                                onClick={() => setPending({ kind: "premium", user, next: !user.is_premium })}
                                style={{
                                  background: user.is_premium ? "var(--warning-light)" : "var(--primary-light)",
                                  color: user.is_premium ? "#b97800" : "var(--primary)",
                                  border: "none",
                                }}
                              >
                                <Crown size={13} />
                              </button>
                              <button
                                className="btn btn-sm"
                                title={user.suspended_at ? "Lift suspension" : "Suspend"}
                                onClick={() =>
                                  setPending({
                                    kind: user.suspended_at ? "unsuspend" : "suspend",
                                    user,
                                  })
                                }
                                style={{
                                  background: user.suspended_at ? "var(--success-light)" : "var(--danger-light)",
                                  color: user.suspended_at ? "var(--success)" : "var(--danger)",
                                  border: "none",
                                }}
                              >
                                {user.suspended_at ? <UserCheck size={13} /> : <Ban size={13} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                    {!list.loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <EmptyState
                            message="No users match this view"
                            hint={search ? `Nothing for “${search}”` : undefined}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={total}
                onChange={setOffset}
                busy={list.fetching}
              />
            </>
          )}
        </div>
      </div>

      <UserDrawer
        open={!!openUser}
        onClose={() => setOpenUser(null)}
        state={detail}
        selfId={identity?.id}
        onAction={setPending}
      />

      {confirmCopy && pending && (
        <ConfirmDialog
          open
          busy={busy}
          title={confirmCopy.title}
          body={confirmCopy.body}
          confirmLabel={confirmCopy.confirmLabel}
          destructive={confirmCopy.destructive}
          requireReason={confirmCopy.requireReason}
          reasonLabel={confirmCopy.reasonLabel}
          onConfirm={runAction}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────────────

function UserDrawer({
  open,
  onClose,
  state,
  selfId,
  onAction,
}: {
  open: boolean;
  onClose: () => void;
  state: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchUserDetail>> | null>>;
  selfId?: string;
  onAction: (action: PendingAction) => void;
}) {
  const data = state.data;
  const profile = data?.profile;

  // The drawer's action buttons need an AdminUser to pass along; the detail
  // payload carries the same identity under a different shape.
  const asUser = profile
    ? ({
        id: profile.id,
        full_name: profile.full_name,
        email: profile.email,
        is_premium: profile.is_premium,
        suspended_at: profile.suspended_at,
        is_admin: profile.is_admin,
      } as AdminUser)
    : null;

  const isSelf = !!profile && profile.id === selfId;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={profile?.full_name ?? "User"}
      subtitle={profile?.email ?? undefined}
      footer={
        asUser && !isSelf ? (
          <>
            <button
              className="btn btn-sm"
              onClick={() => onAction({ kind: "premium", user: asUser, next: !profile!.is_premium })}
            >
              <Crown size={13} /> {profile!.is_premium ? "Revoke Premium" : "Grant Premium"}
            </button>
            <button
              className="btn btn-sm"
              onClick={() =>
                onAction({ kind: profile!.suspended_at ? "unsuspend" : "suspend", user: asUser })
              }
              style={{
                background: profile!.suspended_at ? "var(--success-light)" : "var(--danger-light)",
                color: profile!.suspended_at ? "var(--success)" : "var(--danger)",
                borderColor: "transparent",
              }}
            >
              {profile!.suspended_at ? <UserCheck size={13} /> : <Ban size={13} />}
              {profile!.suspended_at ? "Lift suspension" : "Suspend"}
            </button>
            <button
              className="btn btn-sm"
              onClick={() => onAction({ kind: "admin", user: asUser, next: !profile!.is_admin })}
            >
              <Shield size={13} /> {profile!.is_admin ? "Revoke admin" : "Make admin"}
            </button>
          </>
        ) : isSelf ? (
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            This is your own account — actions are disabled to stop you locking
            yourself out.
          </div>
        ) : null
      }
    >
      {state.error && <ErrorState error={state.error} onRetry={state.refetch} />}
      {state.loading && (
        <div style={{ color: "var(--text-muted)", fontSize: 13, padding: 20 }}>Loading…</div>
      )}

      {data && profile && (
        <>
          {profile.suspended_at && (
            <StatusBanner
              tone="danger"
              title="Suspended"
              body={profile.suspended_reason ?? "No reason recorded."}
              meta={date(profile.suspended_at)}
            />
          )}
          {profile.deactivated_at && (
            <StatusBanner
              tone="warning"
              title="Deletion requested by the user"
              body={
                profile.deletion_reason ??
                "No reason given. Restorable by signing in within 30 days."
              }
              meta={date(profile.deactivated_at)}
            />
          )}

          <Section title="Account">
            <Field label="User ID" value={profile.id} mono />
            <Field label="Username" value={profile.username} />
            <Field label="Phone" value={profile.phone} />
            <Field label="Joined" value={date(profile.created_at)} />
            <Field label="Points" value={profile.points ?? 0} />
            <Field label="Currency" value={profile.currency} />
            <Field
              label="Onboarding"
              value={profile.onboarding_completed ? "Complete" : "Not finished"}
            />
            <Field label="Guide" value={profile.guide_seen_at ? "Seen" : "Not seen"} />
          </Section>

          <Section title="Membership">
            <Field
              label="Plan"
              value={profile.is_premium ? (profile.premium_plan ?? "Premium") : "Free"}
            />
            <Field label="Renews / expires" value={date(profile.premium_expires_at)} />
            <Field label="Stripe customer" value={profile.stripe_customer_id} mono />
          </Section>

          <Section title="Community">
            <Field label="Current streak" value={`${data.live_streak} days`} />
            <Field label="Best streak" value={`${profile.streak_best ?? 0} days`} />
            <Field label="Last active" value={date(profile.streak_last_active_on)} />
            <Field
              label="Zora usage"
              value={
                data.zora_usage
                  ? `${data.zora_usage.message_count} messages · resets ${date(data.zora_usage.next_reset)}`
                  : "Never used"
              }
            />
          </Section>

          {profile.hair_profile && Object.keys(profile.hair_profile).length > 0 && (
            <Section title="Hair profile">
              {Object.entries(profile.hair_profile).map(([key, value]) => (
                <Field
                  key={key}
                  label={key.replace(/_/g, " ")}
                  value={Array.isArray(value) ? value.join(", ") : String(value)}
                />
              ))}
            </Section>
          )}

          {data.billing_address && (
            <Section title="Billing address">
              {Object.entries(data.billing_address)
                .filter(([, value]) => !!value)
                .map(([key, value]) => (
                  <Field key={key} label={key.replace(/_/g, " ")} value={value} />
                ))}
            </Section>
          )}

          <Section title={`Recent orders (${data.orders.length})`}>
            {data.orders.length === 0 && <Muted>No orders yet</Muted>}
            {data.orders.map((order) => (
              <div key={order.id} style={rowStyle}>
                <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                  #{order.id.slice(0, 8).toUpperCase()}
                </span>
                <span className="badge" style={{ fontSize: 10 }}>{order.status}</span>
                <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                  {order.total_amount != null
                    ? new Intl.NumberFormat("en-GB", {
                        style: "currency",
                        currency: (order.currency ?? "GBP").toUpperCase(),
                      }).format(Number(order.total_amount))
                    : "—"}
                </span>
              </div>
            ))}
          </Section>

          <Section title={`Recent posts (${data.posts.length})`}>
            {data.posts.length === 0 && <Muted>No posts yet</Muted>}
            {data.posts.map((post) => (
              <div key={post.id} style={{ ...rowStyle, alignItems: "flex-start" }}>
                <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                  {post.content || <em>Media only</em>}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                    {date(post.created_at)} · ♥ {post.likes_count} · 💬 {post.comments_count}
                    {post.has_media && " · has media"}
                  </div>
                </div>
              </div>
            ))}
          </Section>

          {data.admin_actions.length > 0 && (
            <Section title="Admin history">
              {data.admin_actions.map((entry, index) => (
                <div key={index} style={rowStyle}>
                  <span style={{ fontWeight: 700, fontSize: 12.5 }}>
                    {entry.action.replace(/_/g, " ")}
                  </span>
                  <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                    by {entry.admin ?? "unknown"}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-muted)" }}>
                    {date(entry.created_at)}
                  </span>
                </div>
              ))}
            </Section>
          )}
        </>
      )}
    </Drawer>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 0",
  borderBottom: "1px solid var(--border)",
};

const Muted = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 12.5, color: "var(--text-muted)", padding: "6px 0" }}>{children}</div>
);

function StatusBanner({
  tone,
  title,
  body,
  meta,
}: {
  tone: "danger" | "warning";
  title: string;
  body: string;
  meta?: string;
}) {
  const colors =
    tone === "danger"
      ? { bg: "var(--danger-light)", border: "#f6c9cf", text: "var(--danger)" }
      : { bg: "#fff7ed", border: "#fde0c0", text: "#b4501a" };

  return (
    <div
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "var(--radius)",
        padding: "13px 15px",
        marginBottom: 14,
        display: "flex",
        gap: 10,
      }}
    >
      <Trash2 size={15} color={colors.text} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: colors.text }}>
          {title}
          {meta && (
            <span style={{ fontWeight: 600, opacity: 0.75, marginLeft: 8 }}>{meta}</span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.55 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
